/**
 * Utility functions for filtering and grouping assessment templates by region
 */

/**
 * Determines the region of a template based on its name or industry type
 * @param {Object} template - The template object to check
 * @returns {string} - The region ('uk', 'eu', 'aus', 'dora', or 'general')
 */
export const getTemplateRegion = (template) => {
  if (!template || !template.name) return 'general';
  
  const name = template.name.toLowerCase();
  const industry = template.industryType ? template.industryType.toLowerCase() : '';
  
  // Check for region in template.region first if available
  if (template.region) {
    return template.region.toLowerCase();
  }
  
  // Otherwise check name and industry
  if (name.includes('(uk)') || name.includes('fca') || name.includes('pra') || name.includes('orquest') || industry.includes('uk')) {
    return 'uk';
  } else if (name.includes('(eu)') || name.includes('cca') || name.includes('ai act') || industry.includes('eu')) {
    return 'eu';
  } else if (name.includes('(aus)') || name.includes('(australia)') || name.includes('cirmp') || industry.includes('australia')) {
    return 'aus';
  } else if (name.includes('dora') || name.includes('digital operational resilience') || name.includes('dora resilience assessment')) {
    return 'dora';
  } else {
    return 'general';
  }
};

/**
 * Determines if a template belongs to a specific region based on its name or industry type
 * @param {Object} template - The template object to check
 * @param {string} region - The region to check against ('uk', 'eu', 'aus', 'dora', 'recommended')
 * @returns {boolean} - Whether the template belongs to the specified region
 */
export const isTemplateInRegion = (template, region) => {
  if (!template || !template.name) return false;
  
  // If template has a region property, use that first
  if (template.region && template.region.toLowerCase() === region.toLowerCase()) {
    return true;
  }
  
  const name = template.name.toLowerCase();
  const industry = template.industryType ? template.industryType.toLowerCase() : '';
  
  switch(region) {
    case 'uk':
      return name.includes('(uk)') || 
             name.includes('fca') || 
             name.includes('pra') || 
             name.includes('orquest') || 
             industry.includes('uk');
    case 'eu':
      return name.includes('(eu)') || 
             name.includes('cca') || 
             name.includes('ai act') || 
             industry.includes('eu');
    case 'aus':
      return name.includes('(aus)') || 
             name.includes('(australia)') || 
             name.includes('cirmp') || 
             industry.includes('australia');
    case 'dora':
      return name.includes('dora') || 
             name.includes('digital operational resilience') ||
             name.includes('dora resilience assessment');
    case 'recommended':
      return name.includes('universal') || 
             name.toLowerCase().includes('universal resilience maturity assessment');
    default:
      return true;
  }
};

/**
 * Groups templates by industry within a specific region
 * @param {Array} templates - Array of template objects
 * @param {string} region - The region to filter by ('uk', 'eu', 'aus', 'dora', or 'all')
 * @returns {Object} - Object with industry names as keys and arrays of templates as values
 */
export const groupTemplatesByRegion = (templates, region) => {
  if (!templates || templates.length === 0) {
    console.log('No templates provided to groupTemplatesByRegion');
    return {};
  }

  console.log(`Grouping ${templates.length} templates for region: ${region}`);
  console.log('Templates with DORA region:', templates.filter(t => t.region === 'dora').length);
  console.log('Templates with DORA in name:', templates.filter(t => t.name && t.name.toLowerCase().includes('dora')).length);
  
  // First, filter templates by region if needed
  let filteredTemplates;
  if (region === 'all') {
    filteredTemplates = templates;
  } else {
    // Ensure we're checking both region property and name patterns
    filteredTemplates = templates.filter(template => {
      // First check if template has the exact region property
      if (template.region && template.region.toLowerCase() === region.toLowerCase()) {
        return true;
      }
      
      // Otherwise use the function to check name patterns
      return isTemplateInRegion(template, region);
    });
  }
  
  console.log(`Filtered to ${filteredTemplates.length} templates for region ${region}`);
  
  // Group templates by industry
  const groupedByIndustry = filteredTemplates.reduce((groups, template) => {
    // Normalize industry type based on template name and industry type
    let industry = template.industryType || 'General';
    
    // Special case handling for specific templates
    if (template.name) {
      const name = template.name.toLowerCase();
      if (name.includes('ai act') && name.includes('eu')) {
        industry = 'Information Security';
      } else if (name.includes('cca') && name.includes('eu')) {
        industry = 'Critical Infrastructure';
      } else if (name.includes('energy') && name.includes('utilities')) {
        industry = 'Energy & Utilities';
      } else if (name.includes('financial') || industry === 'Financial') {
        industry = 'Financial Services';
      }
    }
    
    // Map common variations to standardized industry types
    const industryMap = {
      'Financial': 'Financial Services',
      'Finance': 'Financial Services',
      'Banking': 'Financial Services',
      'Energy': 'Energy & Utilities',
      'Utilities': 'Energy & Utilities',
      'Telecom': 'Telecommunications',
      'Telecommunication': 'Telecommunications',
      'Information Technology': 'Technology',
      'IT': 'Technology',
      'Tech': 'Technology',
      'Higher Ed': 'Higher Education',
      'College': 'Higher Education',
      'University': 'Higher Education',
      'Insurance & Risk': 'Insurance',
      'Media': 'Media & Communications',
      'Communications': 'Media & Communications',
      'Legal': 'Legal Services',
      'Law': 'Legal Services',
      'Professional': 'Professional Services',
      'Consulting': 'Professional Services',
      'Real Estate & Property': 'Real Estate',
      'Property Management': 'Real Estate'
    };
    
    // Apply industry mapping if applicable
    if (industryMap[industry]) {
      industry = industryMap[industry];
    }
    
    // Create group if it doesn't exist
    if (!groups[industry]) {
      groups[industry] = [];
    }
    
    // Add template to the appropriate industry group
    groups[industry].push(template);
    return groups;
  }, {});
  
  // Sort industries alphabetically (with General first)
  const sortedIndustries = {};
  
  // First add General if it exists
  if (groupedByIndustry['General']) {
    sortedIndustries['General'] = groupedByIndustry['General'];
    delete groupedByIndustry['General'];
  }
  
  // Then add other industries alphabetically
  Object.keys(groupedByIndustry)
    .sort((a, b) => a.localeCompare(b))
    .forEach(industry => {
      sortedIndustries[industry] = groupedByIndustry[industry];
    });
  
  // Sort templates within each industry alphabetically
  // and put Universal assessment at the top of each group
  Object.keys(sortedIndustries).forEach(industry => {
    sortedIndustries[industry].sort((a, b) => {
      // Check if either template is Universal and put it at the top
      const aIsUniversal = a.name && a.name.toLowerCase().includes('universal');
      const bIsUniversal = b.name && b.name.toLowerCase().includes('universal');
      
      if (aIsUniversal && !bIsUniversal) return -1;
      if (!aIsUniversal && bIsUniversal) return 1;
      
      // Otherwise sort alphabetically by name
      return (a.name || '').localeCompare(b.name || '');
    });
  });
  
  return sortedIndustries;
};

/**
 * Gets default standards text based on region and industry
 * @param {string} region - The region ('uk', 'eu', 'aus', 'dora')
 * @param {string} industry - The industry type
 * @returns {string} - Default standards text
 */
export const getDefaultStandardsForRegion = (region, industry) => {
  const industryLower = (industry || '').toLowerCase();
  
  switch(region) {
    case 'uk':
      if (industryLower.includes('financial')) {
        return 'FCA/PRA Operational Resilience Policy (PS21/3, SS1/21), UK Financial Conduct Authority Guidance';
      }
      return 'UK Operational Resilience Standards, ISO 22301, NIS Regulations';
    
    case 'eu':
      if (industryLower.includes('ai') || industryLower.includes('tech')) {
        return 'EU AI Act, NIS2 Directive, GDPR, ISO 27001';
      }
      return 'EU Critical Infrastructure Directive, NIS2, ISO 22301, GDPR';
    
    case 'aus':
      return 'Australian CIRMP Guidelines, Security of Critical Infrastructure Act, ACSC Essential Eight';
    
    case 'dora':
      return 'EU DORA Regulation (2022/2554), EBA/ESMA/EIOPA Guidelines, NIS2 Directive';
    
    default:
      return 'ISO 22301, ISO 27001, NIST CSF, Industry Best Practices';
  }
};

/**
 * Format standards string by adding proper spacing and formatting
 * @param {string|array} standardsString - Raw standards string or array
 * @returns {string} - Formatted standards string
 */
export const formatStandardsString = (standardsString) => {
  // Handle null, undefined, or empty values
  if (!standardsString) return '';
  
  // Handle array of standards
  if (Array.isArray(standardsString)) {
    return standardsString.join(', ');
  }
  
  // Handle non-string values
  if (typeof standardsString !== 'string') {
    return String(standardsString);
  }
  
  // Replace common separators with properly formatted comma and space
  return standardsString
    .replace(/,\s*/g, ', ')      // Replace comma with optional space with comma + space
    .replace(/\s+/g, ' ')       // Replace multiple spaces with single space
    .replace(/\s*ISO\s*/g, ' ISO ')  // Ensure space before ISO
    .replace(/\s*NIS\s*/g, ' NIS ')  // Ensure space before NIS
    .replace(/\s*NIST\s*/g, ' NIST ')  // Ensure space before NIST
    .replace(/\s*CSF\s*/g, ' CSF ')  // Ensure space before CSF
    .replace(/\s*GDPR\s*/g, ' GDPR ')  // Ensure space before GDPR
    .trim();  // Remove leading/trailing spaces
};

/**
 * Gets default description text based on region and industry
 * @param {string} region - The region ('uk', 'eu', 'aus', 'dora')
 * @param {string} industry - The industry type
 * @returns {string} - Default description text
 */
export const getDefaultDescriptionForRegion = (region, industry) => {
  const industryText = industry || 'organizations';
  
  switch(region) {
    case 'uk':
      return `UK-focused operational resilience assessment for ${industryText} based on FCA/PRA requirements`;
    
    case 'eu':
      return `EU-aligned resilience maturity assessment for ${industryText}`;
    
    case 'aus':
      return `Australian CIRMP-aligned resilience assessment for ${industryText}`;
    
    case 'dora':
      return `DORA compliance assessment for financial ${industryText}`;
    
    default:
      return `Resilience maturity assessment for ${industryText}`;
  }
};
